'use client';

import type {
  ChangeEventHandler,
  ClipboardEventHandler,
  FocusEventHandler,
  KeyboardEventHandler,
  ReactNode,
  RefAttributes,
} from 'react';
import { forwardRef } from 'react';

import { cn } from '../lib/utils';

// ============================================================================
// Types
// ============================================================================

type Props = {
  autoComplete?: string;
  className?: string;
  defaultValue?: string;
  /**
   * Whether the Textarea should fit its parent or its content
   */
  fit?: 'content' | 'parent';
  id?: string;
  isDisabled?: boolean;
  isInvalid?: boolean;
  isReadOnly?: boolean;
  leftAddon?: ReactNode;
  rightAddon?: ReactNode;
  maxLength?: number;
  name?: string;
  onBlur?: FocusEventHandler<HTMLTextAreaElement>;
  onChange?: ChangeEventHandler<HTMLTextAreaElement>;
  onCopy?: ClipboardEventHandler<HTMLTextAreaElement>;
  onFocus?: FocusEventHandler<HTMLTextAreaElement>;
  onKeyDown?: KeyboardEventHandler<HTMLTextAreaElement>;
  onPaste?: ClipboardEventHandler<HTMLTextAreaElement>;
  placeholder?: string;
  /**
   * Minimum number of visible rows. Height auto-grows past this via
   * `field-sizing: content` so the box tracks the content.
   */
  rows?: number;
  value?: string;
  /**
   * Visual style — matches TextInput's variants so the two primitives can
   * sit next to each other without looking out of family.
   */
  variant?: 'default' | 'highlight';
};

// ============================================================================
// TextArea Component
// ============================================================================

const TextAreaBase = forwardRef<HTMLTextAreaElement, Props>(
  (
    {
      autoComplete,
      className,
      defaultValue,
      fit = 'content',
      id,
      isDisabled = false,
      isInvalid = false,
      isReadOnly = false,
      leftAddon,
      rightAddon,
      maxLength,
      name,
      onBlur,
      onChange,
      onCopy,
      onFocus,
      onKeyDown,
      onPaste,
      placeholder,
      rows = 3,
      value,
      variant = 'default',
    },
    ref,
  ) => {
    return (
      <div
        data-slot="text-area-container"
        className={cn(
          // Layout
          'inline-flex items-start',
          // Sizing — textarea grows vertically, so no fixed height
          fit === 'parent' ? 'w-full' : 'w-fit',
          // Styling — mirror TextInput's default container
          'rounded-md border bg-white',
          variant === 'highlight' ? 'border-primary-10' : 'border-primary-20',
          // Focus — same rings/colors as TextInput so the two components
          // read as the same family.
          'transition-all duration-200',
          variant === 'highlight'
            ? 'focus-within:border-highlight-default focus-within:ring-0'
            : 'focus-within:border-primary-40 focus-within:ring-2 focus-within:ring-primary-20/50',
          // States
          isInvalid && 'border-status-danger',
          isDisabled && 'cursor-not-allowed opacity-50',
          className,
        )}
      >
        {leftAddon && (
          <div data-slot="text-area-left-addon" className="flex shrink-0 items-center py-2 pl-2">
            {leftAddon}
          </div>
        )}
        <textarea
          ref={ref}
          id={id}
          name={name}
          value={value}
          defaultValue={defaultValue}
          placeholder={placeholder}
          disabled={isDisabled}
          readOnly={isReadOnly}
          autoComplete={autoComplete}
          maxLength={maxLength}
          rows={rows}
          onBlur={onBlur}
          onChange={onChange}
          onCopy={onCopy}
          onFocus={onFocus}
          onKeyDown={onKeyDown}
          onPaste={onPaste}
          data-slot="text-area"
          // `field-sizing: content` lets the textarea auto-grow as the user
          // types, but when empty it collapses to the intrinsic content size
          // (≈0) and the `rows` attribute stops being honored as a minimum in
          // most browsers. We restore the rows-based minimum explicitly with
          // `lh` (line-height unit) so the default still looks "tall" even
          // when no text is entered yet.
          style={{ minHeight: `calc(${rows} * 1lh + 1rem)` }}
          className={cn(
            // Layout — flex-1 so the textarea fills the container
            'flex-1',
            // Sizing — vertical padding matches h-8 TextInput's optical padding
            'resize-none px-3 py-2',
            fit === 'parent' && 'w-full',
            'field-sizing-content',
            // Typography — same scale as TextInput
            'text-body-13 text-primary-100 placeholder:text-primary-40',
            // Styling
            'bg-transparent',
            // States
            'outline-none focus:ring-0',
            'disabled:cursor-not-allowed',
          )}
        />
        {rightAddon && (
          <div data-slot="text-area-right-addon" className="flex shrink-0 items-center py-2 pr-2">
            {rightAddon}
          </div>
        )}
      </div>
    );
  },
);

export const TextArea: (props: Props & RefAttributes<HTMLTextAreaElement>) => any =
  TextAreaBase as unknown as (props: Props & RefAttributes<HTMLTextAreaElement>) => any;
